import type {
  FieldDef,
  FormSchema,
  QuestionDef,
  ShowWhen,
} from "../risikoanalyseStep/types";

/**
 * Schema for the Transport, Messen & Reisegepäck risk step form.
 *
 * Mirrors `for-ai/risktransport.json`. The JSON nests a custom
 * `transport_direction` type with 3 modes per direction (versendung / bezug),
 * each carrying 6 follow-up fields. We flatten that into one QuestionDef per
 * mode, gated with `showWhenAll` so the mode questions only appear after the
 * master "Umfangreiches Transportrisiko?" answer is "ja".
 */

const yn = (
  question: string,
  key: string,
  followUpKey?: string,
  hint?: string,
): QuestionDef => ({
  question,
  hint,
  field: { key, type: "yesno" },
  ...(followUpKey
    ? {
        showFollowUpWhen: { key, equals: "ja" },
        followUpFields: [
          { key: followUpKey, label: "Kurzbeschreibung", type: "textarea" },
        ] as FieldDef[],
      }
    : {}),
});

const q = (question: string, field: FieldDef, hint?: string): QuestionDef => ({
  question,
  hint,
  field,
});

const INCOTERMS: Array<{ value: string; label: string }> = [
  { value: "exw", label: "EXW – Ab Werk" },
  { value: "fca", label: "FCA – Frei Frachtführer" },
  { value: "cpt", label: "CPT – Frachtfrei" },
  { value: "cip", label: "CIP – Frachtfrei versichert" },
  { value: "dap", label: "DAP – Geliefert benannter Ort" },
  { value: "dpu", label: "DPU – Geliefert entladen" },
  { value: "ddp", label: "DDP – Geliefert verzollt" },
  { value: "fob", label: "FOB – Frei an Bord" },
  { value: "cfr", label: "CFR – Kosten und Fracht" },
  { value: "cif", label: "CIF – Kosten, Versicherung, Fracht" },
  { value: "unbekannt", label: "Nicht bekannt / nicht relevant" },
];

const VERLADUNG_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "eigene", label: "Eigene Mitarbeiter" },
  { value: "fremde", label: "Fremde Mitarbeiter" },
  { value: "gemischt", label: "Eigene und fremde Mitarbeiter" },
];

const FAHRZEUGART_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "pkw", label: "PKW" },
  { value: "transporter", label: "Transporter / Kastenwagen" },
  { value: "lkw", label: "LKW" },
  { value: "anhaenger", label: "Anhänger" },
  { value: "motorrad", label: "Motorrad / Mofa" },
  { value: "sonstiges", label: "Sonstiges" },
];

/**
 * One transport mode (e.g. "Versendung – Eigenständig …").
 *
 * The mode question is a yes/no that, on "ja", reveals 6 follow-up fields
 * (Fahrzeugart, Warenwert, Incoterms, Verladung, Warenart, Verpackungsart).
 * The whole question is gated by the master `tr_01 = ja` answer.
 */
const transportMode = (opts: {
  question: string;
  key: string;
  prefix: string; // used for the follow-up field keys
  fahrzeugartType: "select" | "text";
  fahrzeugartPlaceholder?: string;
  masterGate: ShowWhen;
}): QuestionDef => {
  const fahrzeugart: FieldDef =
    opts.fahrzeugartType === "select"
      ? {
          key: `${opts.prefix}_fahrzeugart`,
          label: "Art des Transportmittels",
          type: "select",
          options: FAHRZEUGART_OPTIONS,
        }
      : {
          key: `${opts.prefix}_fahrzeugart`,
          label: "Art des Transportmittels",
          type: "text",
          placeholder: opts.fahrzeugartPlaceholder,
        };

  return {
    question: opts.question,
    field: { key: opts.key, type: "yesno" },
    showWhen: opts.masterGate,
    showFollowUpWhen: { key: opts.key, equals: "ja" },
    followUpFields: [
      fahrzeugart,
      {
        key: `${opts.prefix}_warenwert`,
        label: "Warenwert (EUR)",
        type: "number",
        placeholder: "z. B. 25000",
      },
      {
        key: `${opts.prefix}_incoterms`,
        label: "Wer trägt die Gefahr (Incoterms)?",
        type: "select",
        options: INCOTERMS,
      },
      {
        key: `${opts.prefix}_verladung`,
        label: "Verladung durch",
        type: "select",
        options: VERLADUNG_OPTIONS,
      },
      {
        key: `${opts.prefix}_warenart`,
        label: "Warenart",
        type: "text",
        placeholder: "z. B. Elektronik, Lebensmittel, Maschinen …",
      },
      {
        key: `${opts.prefix}_verpackung`,
        label: "Verpackungsart",
        type: "text",
        placeholder: "z. B. Karton, Palette, Koffer …",
      },
    ],
  };
};

const MASTER_GATE: ShowWhen = { key: "tr_01", equals: "ja" };

const versendungenQuestions: QuestionDef[] = [
  transportMode({
    question: "Versendung – Eigenständig, mittels eigener Transportmittel?",
    key: "tr_vend_eigen",
    prefix: "tr_vend_eigen",
    fahrzeugartType: "select",
    masterGate: MASTER_GATE,
  }),
  transportMode({
    question: "Versendung – Mittels fremder Transportmittel?",
    key: "tr_vend_fremd",
    prefix: "tr_vend_fremd",
    fahrzeugartType: "text",
    fahrzeugartPlaceholder: "z. B. gemieteter LKW, Leihfahrzeug …",
    masterGate: MASTER_GATE,
  }),
  transportMode({
    question: "Versendung – Durch Dritte (z. B. Spedition)?",
    key: "tr_vend_dritte",
    prefix: "tr_vend_dritte",
    fahrzeugartType: "text",
    fahrzeugartPlaceholder: "z. B. Spedition, Kurierdienst, Bahn …",
    masterGate: MASTER_GATE,
  }),
];

const bezuegeQuestions: QuestionDef[] = [
  transportMode({
    question: "Bezug – Eigenständig, mittels eigener Transportmittel?",
    key: "tr_bez_eigen",
    prefix: "tr_bez_eigen",
    fahrzeugartType: "select",
    masterGate: MASTER_GATE,
  }),
  transportMode({
    question: "Bezug – Mittels fremder Transportmittel?",
    key: "tr_bez_fremd",
    prefix: "tr_bez_fremd",
    fahrzeugartType: "text",
    fahrzeugartPlaceholder: "z. B. gemieteter LKW, Leihfahrzeug …",
    masterGate: MASTER_GATE,
  }),
  transportMode({
    question: "Bezug – Durch Dritte (z. B. Spedition)?",
    key: "tr_bez_dritte",
    prefix: "tr_bez_dritte",
    fahrzeugartType: "text",
    fahrzeugartPlaceholder: "z. B. Spedition, Kurierdienst, Bahn …",
    masterGate: MASTER_GATE,
  }),
];

export const transportStepFormSchema: FormSchema = {
  sections: [
    {
      key: "transport",
      title: "Transport-Risiken",
      color: "pillarOrange",
      subSteps: [
        {
          key: "transport_uebersicht",
          title: "Übersicht – Transportrisiko",
          description:
            "Bei speziellen Risiken (Transport-General-Police, Spedition, Kunst, Valoren etc.) ist ein separater Risikofragebogen erforderlich.",
          questions: [
            {
              question:
                "Umfangreiches oder spezielles Transportrisiko vorhanden?",
              hint: "Transportumsatz, versicherte Güter, Geltungsbereich, Transportmittel, Versicherungssummen, Franchisen etc.",
              field: { key: "tr_01", type: "yesno" },
              showFollowUpWhen: { key: "tr_01", equals: "ja" },
              followUpFields: [
                {
                  key: "tr_kurzbeschreibung",
                  label: "Kurzbeschreibung",
                  type: "textarea",
                },
              ],
            },
          ],
        },
        {
          key: "transport_versendungen",
          title: "Versendungen",
          description:
            "Wie kommen Waren zum Empfänger? Wer trägt die Gefahr? (Nur sichtbar, wenn ein Transportrisiko vorhanden ist.)",
          questions: versendungenQuestions,
        },
        {
          key: "transport_bezuege",
          title: "Bezüge",
          description:
            "Wie kommen Waren zu Ihnen? Wer trägt die Gefahr? (Nur sichtbar, wenn ein Transportrisiko vorhanden ist.)",
          questions: bezuegeQuestions,
        },
      ],
    },
    {
      key: "messen",
      title: "Messen / Ausstellungen",
      color: "blue",
      subSteps: [
        {
          key: "messen",
          title: "Messen / Ausstellungen",
          description:
            "Teilnahme an Messen oder Ausstellungen.",
          questions: [
            {
              question: "Nehmen Sie an Messen oder Ausstellungen teil?",
              field: { key: "mes_01", type: "yesno" },
              showFollowUpWhen: { key: "mes_01", equals: "ja" },
              followUpFields: [
                {
                  key: "mes_01_welche",
                  label: "Welche Messen / Ausstellungen?",
                  type: "textarea",
                  placeholder: "z. B. Hannover Messe, BAU München …",
                },
                {
                  key: "mes_01_anzahl",
                  label: "Wie viele pro Jahr?",
                  type: "number",
                  placeholder: "Veranstaltungen / Jahr",
                },
                {
                  key: "mes_01_wo",
                  label: "Wo? (Standorte / Regionen)",
                  type: "text",
                  placeholder: "z. B. Deutschland, Europa, weltweit …",
                },
                {
                  key: "mes_01_versicherungssumme",
                  label: "Versicherungssumme (EUR)",
                  type: "number",
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: "reisegepaeck",
      title: "Reisegepäck",
      color: "green",
      subSteps: [
        {
          key: "reisegepaeck",
          title: "Reisegepäck",
          description:
            "Absicherung von Reisegepäck der Mitarbeitenden.",
          questions: [
            {
              question: "Ist Reisegepäck von Mitarbeitenden abzusichern?",
              field: { key: "rg_01", type: "yesno" },
              showFollowUpWhen: { key: "rg_01", equals: "ja" },
              followUpFields: [
                {
                  key: "rg_01_versicherungssumme",
                  label: "Versicherungssumme (EUR)",
                  type: "number",
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

// Re-export helpers for any future consumers / tests.
export const __transportSchemaInternals = {
  yn,
  q,
  transportMode,
  INCOTERMS,
  VERLADUNG_OPTIONS,
  FAHRZEUGART_OPTIONS,
};
